import React, { useMemo } from "react";
import { isEmpty } from "lodash";
import { RadioGroup, RadioGroupItem } from "@picklebet/ui";
import type { TokenAutomation } from "hooks/firestore/models/tokenAutomation";
import type { TokenAllocations } from "@/features/betslip/hooks/useTokenAllocations";

import {
  beautifyNumbers,
  pickLegBasedCopy,
  getLegBasedBetslipInfo,
} from "@/features/betslip/utils/tokenFormatting";
import {
  byAllocationPriority,
  byBetslipPriority,
} from "@/features/betslip/utils/tokenSort";

import * as styles from "@/features/betslip/components/parts/tokens/Tokens.module.scss";

type AllocationPickerProps = {
  isMulti: boolean;
  legCount: number;
  outcomeIds: string[];
  selectedAllocationId: string | null | undefined;
  multiTokenAllocationId: string | null | undefined;
  selectedAutomationType: string | undefined;
  sortedAllocations: TokenAllocations;
  sortedAutomations: TokenAutomation[];
  onPick: (allocation: TokenAllocations[number]) => void;
  onDeselect: () => void;
};

export const AllocationPicker = ({
  isMulti,
  legCount,
  outcomeIds,
  selectedAllocationId,
  multiTokenAllocationId,
  selectedAutomationType,
  sortedAllocations,
  sortedAutomations,
  onPick,
  onDeselect,
}: AllocationPickerProps) => {
  const automationsForSelectedType = useMemo(() => {
    if (!selectedAutomationType) return [];
    return sortedAutomations.filter((automation) => {
      const action = automation.actions?.[0];
      const hasAllocations = sortedAllocations.some(
        (a) => a.automationId === automation.automationId,
      );
      return action?.type === selectedAutomationType && hasAllocations;
    });
  }, [selectedAutomationType, sortedAutomations, sortedAllocations]);

  if (!automationsForSelectedType.length) return null;

  return (
    <RadioGroup
      onValueChange={(allocationId) => {
        const selectedAllocation = sortedAllocations.find(
          (a) => a.id === allocationId,
        );
        const allocationsForSelectedAutomation = sortedAllocations.filter(
          (a) => a.automationId === selectedAllocation?.automationId,
        );
        const automation = automationsForSelectedType.find(
          (a) => a.automationId === selectedAllocation?.automationId,
        );

        if (selectedAllocationId === allocationId && selectedAllocationId) {
          onDeselect();
          return;
        }

        const firstAllocation =
          allocationsForSelectedAutomation.find(
            (a) =>
              !a.usedAtOutcomeId &&
              (a?.actions?.[0]?.info ?? automation?.actions?.[0]?.info) ===
                selectedAllocation?.actions?.[0]?.info,
          ) ?? allocationsForSelectedAutomation[0];

        if (!firstAllocation) return;
        onPick(firstAllocation);
      }}
    >
      {automationsForSelectedType.sort(byBetslipPriority).map((automation) => {
        const legBasedBetslipCopy = getLegBasedBetslipInfo(
          legCount,
          automation?.legBasedBetSlipInfo,
        );

        const sortedAllocationsForAutomation = sortedAllocations
          .filter((a) => a.automationId === automation.automationId)
          .sort(byAllocationPriority);

        const allocationsByInfo = sortedAllocationsForAutomation.reduce(
          (acc, allocation) => {
            const infoKey =
              allocation?.actions?.[0]?.info ?? automation?.actions?.[0]?.info;
            if (infoKey) {
              if (!acc[infoKey]) acc[infoKey] = [allocation];
              else acc[infoKey].push(allocation);
            }
            return acc;
          },
          {} as Record<string, TokenAllocations>,
        );

        return Object.keys(allocationsByInfo).map((allocationInfo) => {
          const firstAllocation =
            allocationsByInfo[allocationInfo].find(
              (a) =>
                a.usedAtOutcomeId === outcomeIds[0] ||
                (isMulti && multiTokenAllocationId === a.id),
            ) ?? allocationsByInfo[allocationInfo][0];

          const isChecked = selectedAllocationId === firstAllocation.id;

          const completeBetSlipInfo = !isEmpty(
            firstAllocation?.completeBetSlipInfo,
          )
            ? firstAllocation.completeBetSlipInfo
            : automation?.completeBetSlipInfo;

          const finalCopy =
            pickLegBasedCopy(completeBetSlipInfo, legCount) ??
            `${allocationInfo}${legBasedBetslipCopy ? ` ${legBasedBetslipCopy}` : ""}`;

          return (
            <RadioGroupItem
              key={`allocation-${firstAllocation.id}-${outcomeIds[0]}`}
              id={`allocation-${firstAllocation.id}-${outcomeIds[0]}`}
              size="small"
              checked={isChecked}
              value={firstAllocation.id}
              label={
                <>
                  {automation.attributes?.tokenInfoPrefix && (
                    <span className={styles.prefix}>
                      {automation.attributes.tokenInfoPrefix}:{" "}
                    </span>
                  )}
                  <span
                    className={styles.label}
                    dangerouslySetInnerHTML={{
                      __html: beautifyNumbers(finalCopy),
                    }}
                  />
                </>
              }
              labelComponent="span"
              labelProps={{
                onClick: () => {
                  if (isChecked && selectedAllocationId) onDeselect();
                },
                onMouseDown: (e) => e.preventDefault(),
              }}
              onClick={() => {
                if (isChecked && selectedAllocationId) onDeselect();
              }}
              onMouseDown={(e) => e.preventDefault()}
            ></RadioGroupItem>
          );
        });
      })}
    </RadioGroup>
  );
};
